import React, {useCallback, useEffect, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '../../../config/analytics/analytics';
import {Box} from '@buncha/components/core/view';
import {ClientType} from '@buncha/common/types/common';
import {CustomFlatList} from '@buncha/components/core/list';
import {EventType} from '../../../config/analytics/type';
import {H6Text} from '@buncha/components/core/text';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import LocateItemCell from './LocateItemCell';
import {Modules} from '@buncha/utils/types';
import {RunForBatchDetail} from '../../../batchDetails/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {StyleSheet} from 'react-native';
import {navigation} from '@buncha/config/navigation';
import {useAdjacentOOSItems} from '@buncha/batchDetails/hooks/BatchDetails';
import {useBatchDetailContext} from '../../../batchDetails/hooks/batchDetail';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface TodoItemListProps {
  runDetail?: RunForBatchDetail;
  sentList?: boolean;
  moveToDone: () => void;
}

function LocateItemList(props: TodoItemListProps) {
  const {runDetail, sentList, moveToDone} = props;
  const route = useRoute();
  const font = useFontTheme();
  const theme = useColorThemeType();
  const routeParams: {runId?: number} | undefined = route?.params;
  const runId = Number(routeParams?.runId);
  const {state, dispatch} = useBatchDetailContext();
  const {getAdjacentOOSItems} = useAdjacentOOSItems(dispatch);
  const [aoosChecking, setAoosChecking] = useState(false);

  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const onClick = useCallback(
    async (
      catalogItemId: number,
      orderItemId: number,
      adjacentOOSId: number,
    ) => {
      setAoosChecking(true);
      const res = await getAdjacentOOSItems(
        props.sentList ? 'sent' : 'locate',
        runId,
      );
      const isValid = res?.find(item => item.adjacentOOS?.id === adjacentOOSId);
      setAoosChecking(false);
      if (!isValid) return;
      if (props.sentList)
        navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.ItemDetailsPage,
          params: {
            catalogItemId,
            orderItemId,
            storeIndex: runDetail?.store?.elasticSearchIndex,
            runId: runDetail?.id,
            storeId: runDetail?.store?.id,
          },
        });
      else
        navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.SubstituteItemDetailPage,
          params: {
            catalogItemId,
            orderItemId,
            storeIndex: runDetail?.store?.elasticSearchIndex,
            runId: runDetail?.id,
            adjacentOOSId,
          },
        });
    },
    [
      getAdjacentOOSItems,
      props.sentList,
      runDetail?.id,
      runDetail?.store?.elasticSearchIndex,
      runDetail?.store?.id,
      runId,
    ],
  );

  useEffect(() => {
    Analytics.eventWithProps('Locate Tab', {
      type: EventType.cta,
      runID: runId,
      totalItem: state?.groupedAssignedItems?.locate?.length || 0,
      client: ClientType.buncha,
    });
    getAdjacentOOSItems(sentList ? 'sent' : 'locate', runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Separator = useCallback(() => <Box style={styles.divider} />, []);
  const renderItem = useCallback(
    ({item}: {item: any; index: number}) => (
      <LocateItemCell
        item={item}
        onClick={onClick}
        aoosChecking={aoosChecking}
        sentCell={props.sentList}
      />
    ),
    [onClick, props.sentList, aoosChecking],
  );
  const listData = props.sentList
    ? state.groupedAssignedItems?.sent || []
    : state.groupedAssignedItems?.locate || [];

  useEffect(() => {
    if (!listData?.length) moveToDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData]);

  return (
    <Box style={styles.listContainer}>
      <CustomFlatList
        data={listData}
        renderItem={renderItem}
        ListHeaderComponent={
          <Box style={styles.headerBox}>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: font[700].normal,
                  color: theme?.text.blue[300],
                },
              ]}>
              {props.sentList ? body?.sentList : body?.notSentList}
            </H6Text>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: font[600].normal,
                },
              ]}>
              {props.sentList
                ? body?.sentListSubHeader
                : body?.notSentListSubHeader}
            </H6Text>
          </Box>
        }
        ItemSeparatorComponent={Separator}
        contentContainerStyle={styles.listContentContainer}
        showsVerticalScrollIndicator={false}
      />
    </Box>
  );
}

export default React.memo(LocateItemList);

const styles = StyleSheet.create({
  listContainer: {paddingHorizontal: 15, gap: 5},
  divider: {
    marginVertical: 10,
  },
  textAlignLeft: {
    textAlign: 'left',
  },
  listContentContainer: {
    paddingBottom: 200,
    marginTop: 10,
  },
  headerBox: {
    marginBottom: 10,
  },
});
